// Runner / platformer simulation (7.6F.1, ADR-0023, INTERACTION-MODEL §7.6).
// Pure and deterministic like `game.ts`: the Studio preview uses it today and
// the Delivery Runtime (7.6H) must use THE SAME code — never a reimplementation.
//
// Fixed step: the simulation only ever advances by RUNNER_STEP_MS. The host
// calls `step()` once per elapsed step of ITS logical clock, so a slow browser
// that draws fewer frames runs the same steps and gets the same contacts. The
// host clamps the time one frame may catch up (RUNNER_MAX_CATCH_UP_MS): a long
// stall slows the game down instead of jumping ahead (bounded CPU per frame).
//
// What it decides: the player's pose and height (a parabolic jump, a duck that
// shrinks the hit box), how far the world has scrolled (per segment speed), the
// layer offsets (parallax, seamless loops), the current environment segment and
// its scenery cross-fade, spawned instances (seeded intervals and templates,
// bounded pools, despawn once off the game area), placed course items and the
// axis-aligned contacts between the player's hit box and every live object. It
// returns GameEffects (energy / score / lives deltas, `game.event` signals,
// counted interactions); the host applies them through the declared variables.
//
// Nothing here reads a clock, the DOM, Math.random or the network, and nothing
// mutates the Creative Document: instances are ephemeral session objects made
// from declared templates. Geometry is the ACTIVE format's explicit frames.

import type { GameEffect, Rect } from "./game.js";
import type { RunnerDecl, RunnerEffect, RunnerObjectKind, RunnerSpawn } from "./schema/document.js";
import { ENGINE_LIMITS } from "./limits.js";

/** Simulation step (ms): 60 steps per logical second, whatever the display rate. */
export const RUNNER_STEP_MS = 1_000 / 60;
/** Most logical time one rendered frame may catch up; hosts clamp a longer frame to this (slow-down, never skip). */
export const RUNNER_MAX_CATCH_UP_MS = 100;
/** Upper bound of hit tests in one step: player vs every live instance and placed item. */
export const RUNNER_MAX_COLLISION_CHECKS = ENGINE_LIMITS.runnerActiveMax + ENGINE_LIMITS.runnerItems;
/** Shortest visible "hit" pose, also when no invulnerability is declared. */
const HIT_POSE_MS = 400;

export type RunnerPose = "idle" | "running" | "jumping" | "ducking" | "hit" | "completed";

export interface RunnerGeometry {
  /** Size of the active format variant. */
  readonly canvas: { readonly width: number; readonly height: number };
  /** Explicit frame of a top-level layer in the active variant (undefined = absent from it). */
  readonly frame: (id: string) => Rect | undefined;
  /** Id of the active layout (picks `speedByLayout`). */
  readonly layoutId: string;
}

/** A live spawned object (ephemeral: never part of the document). */
export interface RunnerInstance {
  /** Session-unique, increasing. */
  readonly key: number;
  readonly spawn: string;
  readonly kind: RunnerObjectKind;
  readonly template: string;
  /** Stable render slot of this template (0 … maxActive − 1). */
  readonly slot: number;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

interface LiveInstance {
  key: number;
  spawn: RunnerSpawn;
  template: string;
  slot: number;
  x: number;
  prevX: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  touched: boolean;
}

interface ItemState {
  gone: boolean;
  touched: boolean;
  prevX: number;
}

/** Swept horizontal extent: an object moving fast never tunnels through the player between two steps. */
function sweptOverlap(a: Rect, x: number, prevX: number, b: { y: number; width: number; height: number }): boolean {
  const left = Math.min(x, prevX);
  const right = Math.max(x, prevX) + b.width;
  return a.x < right && left < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height;
}

/** Positive modulo in [0, m). */
const mod = (v: number, m: number) => ((v % m) + m) % m;

/**
 * One runner in one ad instance and one format variant. Created by GameSession
 * (same lifecycle: start / reset / end); the host drives `step()` on its clock.
 */
export class RunnerSession {
  readonly decl: RunnerDecl;
  readonly #geo: RunnerGeometry;
  readonly #present: (id: string) => boolean;
  #running = false;
  #ended = false;
  #steps = 0;
  #distance = 0;
  // Player.
  #jumpStart: number | null = null;
  #duckUntil: number | null = null;
  #duckHeld = false;
  #invulnerableUntil = -Infinity;
  #hitUntil = -Infinity;
  #y = 0;
  // World.
  #segment = 0;
  #segmentSince = 0;
  #previousSegment: number | null = null;
  #instances: LiveInstance[] = [];
  #nextKey = 1;
  readonly #nextSpawnAt = new Map<string, number>();
  readonly #items = new Map<string, ItemState>();
  #drain = 0;
  /** Incremented on every DISCRETE change (pose, segment, an object appearing or leaving): hosts re-render on it. */
  version = 0;

  constructor(decl: RunnerDecl, geometry: RunnerGeometry, present: (id: string) => boolean = () => true) {
    this.decl = decl;
    this.#geo = geometry;
    this.#present = present;
    this.#y = this.#ground();
  }

  // ─── Queries ──────────────────────────────────────────────────────────────

  /** Run time (ms) = steps × RUNNER_STEP_MS. */
  get time(): number {
    return this.#steps * RUNNER_STEP_MS;
  }
  get steps(): number {
    return this.#steps;
  }
  get running(): boolean {
    return this.#running;
  }
  /** World scrolled so far (px of the active format). */
  get distance(): number {
    return this.#distance;
  }
  /** Total course duration (the sum of the segments). */
  get courseMs(): number {
    return this.decl.segments.reduce((sum, s) => sum + s.durationMs, 0);
  }
  /** The course has been run to its end (win `finish`). */
  get finished(): boolean {
    return this.time >= this.courseMs;
  }
  get segmentIndex(): number {
    return this.#segment;
  }
  get segmentId(): string {
    return this.decl.segments[this.#segment]?.id ?? "";
  }
  /** World speed in px/s of the active format at the current segment. */
  speed(): number {
    const base = this.decl.speedByLayout?.[this.#geo.layoutId] ?? this.decl.speed;
    return base * (this.decl.segments[this.#segment]?.speed ?? 1);
  }

  get pose(): RunnerPose {
    if (this.#ended) return "completed";
    if (!this.#running) return "idle";
    if (this.time < this.#hitUntil) return "hit";
    if (this.#jumpStart !== null) return "jumping";
    if (this.#duckUntil !== null) return "ducking";
    return "running";
  }
  /** Declared element state for the current pose (undefined = the player's own presentation). */
  poseState(): string | undefined {
    const s = this.decl.player.states;
    switch (this.pose) {
      case "hit":
        return s?.hit ?? (this.#jumpStart !== null ? s?.jump : this.#duckUntil !== null ? s?.duck : undefined);
      case "jumping":
        return s?.jump;
      case "ducking":
        return s?.duck;
      default:
        return undefined;
    }
  }
  get airborne(): boolean {
    return this.#jumpStart !== null;
  }
  get invulnerable(): boolean {
    return this.time < this.#invulnerableUntil;
  }

  #frame(): Rect | undefined {
    return this.#geo.frame(this.decl.player.element);
  }
  #ground(): number {
    return this.#frame()?.y ?? 0;
  }

  /** Top of the player's drawn frame now (its authored x never changes). */
  playerY(): number {
    return this.#y;
  }

  /** The player's hit box now: inset, raised by the jump, shortened (bottom anchored) while ducking. */
  hitbox(): Rect | undefined {
    const f = this.#frame();
    if (!f) return undefined;
    const ix = (this.decl.player.hitbox?.x ?? 0) * f.width;
    const iy = (this.decl.player.hitbox?.y ?? 0) * f.height;
    const top = this.#y + iy;
    const bottom = this.#y + f.height - iy;
    let height = Math.max(1, bottom - top);
    let y = top;
    const duck = this.decl.player.duck;
    if (this.#duckUntil !== null && duck) {
      const kept = height * duck.height;
      y = bottom - kept;
      height = kept;
    }
    return { x: f.x + ix, y, width: Math.max(1, f.width - 2 * ix), height };
  }

  /** Where a parallax layer is drawn now; a looping layer also draws a copy right after itself. */
  layerX(id: string): { readonly x: number; readonly copy?: number } | undefined {
    const layer = this.decl.layers?.find((l) => l.element === id);
    const f = this.#geo.frame(id);
    if (!layer || !f) return undefined;
    const shift = this.#sign() * this.#distance * layer.factor;
    if (!layer.loop || f.width <= 0) return { x: f.x + shift };
    const x = f.x - mod(-shift, f.width);
    return { x, copy: x + f.width };
  }

  /** Placed course item: its x now (undefined once collected / out of the run, or not an item). */
  itemX(id: string): number | undefined {
    const f = this.#geo.frame(id);
    const item = this.decl.items?.find((i) => i.element === id);
    if (!f || !item || this.#items.get(id)?.gone) return undefined;
    return f.x + this.#sign() * this.#distance;
  }
  itemGone(id: string): boolean {
    return this.#items.get(id)?.gone ?? false;
  }

  instances(): readonly RunnerInstance[] {
    return this.#instances.map((i) => ({
      key: i.key,
      spawn: i.spawn.id,
      kind: i.spawn.kind,
      template: i.template,
      slot: i.slot,
      x: i.x,
      y: i.y,
      width: i.width,
      height: i.height,
    }));
  }

  /**
   * Opacity factor of a segment's scenery layer now (1 shown, 0 hidden, in between while
   * cross-fading); undefined = not scenery of any segment (the layer is not managed).
   */
  sceneryOpacity(id: string): number | undefined {
    const owners = this.decl.segments.flatMap((s, i) => (s.show.includes(id) ? [i] : []));
    if (owners.length === 0) return undefined;
    const current = owners.includes(this.#segment);
    const fade = this.decl.fadeMs;
    const since = this.time - this.#segmentSince;
    const p = fade <= 0 || this.#previousSegment === null ? 1 : Math.min(1, since / fade);
    if (current) return this.#previousSegment !== null && owners.includes(this.#previousSegment) ? 1 : p;
    if (this.#previousSegment !== null && owners.includes(this.#previousSegment)) return 1 - p;
    return 0;
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  start(random: () => number): void {
    this.reset();
    this.#running = true;
    for (const s of this.decl.spawns ?? []) this.#nextSpawnAt.set(s.id, (s.startMs ?? 0) + this.#interval(s, random));
    this.version += 1;
  }

  reset(): void {
    this.#running = false;
    this.#ended = false;
    this.#steps = 0;
    this.#distance = 0;
    this.#jumpStart = null;
    this.#duckUntil = null;
    this.#duckHeld = false;
    this.#invulnerableUntil = -Infinity;
    this.#hitUntil = -Infinity;
    this.#y = this.#ground();
    this.#segment = 0;
    this.#segmentSince = 0;
    this.#previousSegment = null;
    this.#instances = [];
    this.#nextKey = 1;
    this.#nextSpawnAt.clear();
    this.#items.clear();
    this.#drain = 0;
    this.version += 1;
  }

  /** The round ended: the world stops where it is, the player shows its completed pose. */
  end(): void {
    if (!this.#running) return;
    this.#running = false;
    this.#ended = true;
    this.#duckHeld = false;
    this.version += 1;
  }

  // ─── Inputs (player gestures; ignored unless running) ─────────────────────

  /** Jump: only from the ground (no double jump); ends a duck. */
  jump(): GameEffect[] {
    if (!this.#running || this.#jumpStart !== null) return [];
    this.#jumpStart = this.time;
    this.#duckUntil = null;
    this.#duckHeld = false;
    this.version += 1;
    return [
      { kind: "interaction", interaction: "tap" },
      { kind: "signal", event: "jump", gesture: true },
    ];
  }

  /**
   * Duck pressed / released. A press on the ground ducks for at least `duck.durationMs`
   * and as long as it is held; in the air it is ignored. No duck declared = no-op.
   */
  duck(pressed: boolean): GameEffect[] {
    const duck = this.decl.player.duck;
    if (!this.#running || !duck) return [];
    if (!pressed) {
      this.#duckHeld = false;
      return [];
    }
    if (this.#jumpStart !== null || this.#duckUntil !== null) return [];
    this.#duckUntil = this.time + duck.durationMs;
    this.#duckHeld = true;
    this.version += 1;
    return [
      { kind: "interaction", interaction: "tap" },
      { kind: "signal", event: "duck", gesture: true },
    ];
  }

  // ─── Simulation ───────────────────────────────────────────────────────────

  /** Advances exactly one fixed step. Bounded work: ≤ RUNNER_MAX_COLLISION_CHECKS hit tests. */
  step(random: () => number): GameEffect[] {
    if (!this.#running) return [];
    const out: GameEffect[] = [];
    this.#steps += 1;
    const t = this.time;
    const dt = RUNNER_STEP_MS / 1_000;

    // Player: jump arc (deterministic parabola), duck end.
    const f = this.#frame();
    const ground = f?.y ?? 0;
    if (this.#jumpStart !== null && f) {
      const p = (t - this.#jumpStart) / this.decl.player.jump.durationMs;
      if (p >= 1) {
        this.#jumpStart = null;
        this.#y = ground;
        this.version += 1;
      } else this.#y = ground - this.decl.player.jump.height * f.height * 4 * p * (1 - p);
    } else this.#y = ground;
    if (this.#duckUntil !== null && !this.#duckHeld && t >= this.#duckUntil) {
      this.#duckUntil = null;
      this.version += 1;
    }
    const wasHit = t - RUNNER_STEP_MS < this.#hitUntil;
    if (wasHit && t >= this.#hitUntil) this.version += 1;

    // Environment segment (by run time; the last one stays once the course is over).
    let acc = 0;
    let index = this.decl.segments.length - 1;
    for (const [i, s] of this.decl.segments.entries()) {
      acc += s.durationMs;
      if (t < acc) {
        index = i;
        break;
      }
    }
    if (index !== this.#segment) {
      this.#previousSegment = this.#segment;
      this.#segment = index;
      this.#segmentSince = t;
      this.version += 1;
      out.push({ kind: "signal", event: "segment", target: this.segmentId, gesture: false });
    }

    // World.
    const step = this.speed() * dt;
    this.#distance += step;
    const sign = this.#sign();
    const { width } = this.#geo.canvas;

    // Spawned instances move; those fully off the game area leave.
    const before = this.#instances.length;
    for (const i of this.#instances) {
      i.prevX = i.x;
      i.x += sign * step * (i.spawn.speed ?? 1);
    }
    this.#instances = this.#instances.filter((i) => (sign < 0 ? i.x + i.width > 0 : i.x < width));
    if (this.#instances.length !== before) this.version += 1;

    // Spawn rules (seeded interval and template; bounded pools: a full pool skips, never queues).
    for (const s of this.decl.spawns ?? []) {
      const due = this.#nextSpawnAt.get(s.id);
      if (due === undefined || t < due) continue;
      this.#nextSpawnAt.set(s.id, due + this.#interval(s, random));
      if (!this.#eligible(s, t)) continue;
      const template = s.templates.length > 1 ? s.templates[Math.floor(random() * s.templates.length)] : s.templates[0];
      if (template === undefined) continue;
      this.#spawn(s, template);
    }

    // Placed items scroll in with the ground.
    for (const item of this.decl.items ?? []) {
      const st = this.#items.get(item.element) ?? { gone: false, touched: false, prevX: Number.NaN };
      this.#items.set(item.element, st);
      if (st.gone) continue;
      const fr = this.#geo.frame(item.element);
      if (!fr) continue;
      const x = fr.x + sign * this.#distance;
      st.prevX = fr.x + sign * (this.#distance - step);
      if (sign < 0 ? x + fr.width <= 0 : x >= width) {
        st.gone = true;
        this.version += 1;
      }
    }

    // Contacts (axis-aligned, swept horizontally).
    const box = this.hitbox();
    if (box) {
      for (const i of [...this.#instances]) {
        if (i.touched || !sweptOverlap(box, i.x, i.prevX, i)) continue;
        out.push(...this.#contact(i.spawn.kind, i.spawn.effect, i.template));
        i.touched = true;
        if (i.spawn.kind !== "obstacle") {
          this.#instances = this.#instances.filter((x) => x !== i);
          this.version += 1;
        }
      }
      for (const item of this.decl.items ?? []) {
        const st = this.#items.get(item.element);
        const fr = this.#geo.frame(item.element);
        if (!st || st.gone || st.touched || !fr) continue;
        const x = fr.x + sign * this.#distance;
        if (!sweptOverlap(box, x, st.prevX, fr)) continue;
        out.push(...this.#contact(item.kind, item.effect, item.element));
        st.touched = true;
        if (item.kind !== "obstacle") {
          st.gone = true;
          this.version += 1;
        }
      }
    }

    // Energy drain (whole units only: never a variable change per step).
    const drain = this.decl.energy?.drainPerSecond ?? 0;
    if (drain > 0) {
      this.#drain += drain * dt;
      const whole = Math.floor(this.#drain);
      if (whole >= 1) {
        this.#drain -= whole;
        out.push({ kind: "energy", delta: -whole });
      }
    }
    return out;
  }

  #sign(): number {
    return this.decl.direction === "left" ? -1 : 1;
  }

  #interval(s: RunnerSpawn, random: () => number): number {
    const lo = Math.min(s.everyMs.min, s.everyMs.max);
    const hi = Math.max(s.everyMs.min, s.everyMs.max);
    return lo + Math.floor(random() * (hi - lo + 1));
  }

  #eligible(s: RunnerSpawn, t: number): boolean {
    if (s.endMs !== undefined && t >= s.endMs) return false;
    if (s.segments !== undefined && !s.segments.includes(this.segmentId)) return false;
    return this.#instances.length < ENGINE_LIMITS.runnerActiveMax;
  }

  #spawn(s: RunnerSpawn, template: string): void {
    const fr = this.#geo.frame(template);
    if (!fr || !this.#present(template)) return;
    const mine = this.#instances.filter((i) => i.spawn.id === s.id);
    if (mine.length >= s.maxActive) return;
    const used = new Set(mine.filter((i) => i.template === template).map((i) => i.slot));
    let slot = 0;
    while (used.has(slot)) slot++;
    const x = this.#sign() < 0 ? this.#geo.canvas.width : -fr.width;
    this.#instances.push({
      key: this.#nextKey++,
      spawn: s,
      template,
      slot,
      x,
      prevX: x,
      y: fr.y,
      width: fr.width,
      height: fr.height,
      touched: false,
    });
    this.version += 1;
  }

  #contact(kind: RunnerObjectKind, effect: RunnerEffect, target: string): GameEffect[] {
    const out: GameEffect[] = [];
    const t = this.time;
    if (kind !== "pickup") {
      if (t < this.#invulnerableUntil) return out; // passes through while invulnerable
      this.#invulnerableUntil = t + this.decl.player.invulnerableMs;
      this.#hitUntil = t + Math.max(HIT_POSE_MS, this.decl.player.invulnerableMs);
      this.version += 1;
    }
    if (effect.energy !== undefined && effect.energy !== 0) out.push({ kind: "energy", delta: effect.energy });
    if (effect.score !== undefined && effect.score !== 0) out.push({ kind: "score", delta: effect.score });
    if (effect.lives !== undefined && effect.lives !== 0) out.push({ kind: "lives", delta: effect.lives });
    out.push({ kind: "signal", event: kind === "pickup" ? "pickup" : "hazard", target, gesture: false });
    return out;
  }
}
